<script lang="ts" setup>
import { showImagePreview, Tag } from "vant";
import { computed, onActivated, onMounted, ref } from "vue";
import UserAvatar from "@/components/UserAvatar.vue";
import { useRawImageUrl, useVisibilityText } from "@/use/common";
import ChatMessageItem from "@/components/mobile/ChatMessageItem.vue";
import { useRoute, useRouter } from "vue-router";
import { isImage, timeAgo } from "@/modules/common";
import {
  ChatMessage,
  Mate,
  Media,
  Moment,
  Reply,
  Visibility,
} from "@/types/entity";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import ReactionCounter from "@/components/mobile/reply/ReactionCounter.vue";
import ReactionMate from "@/components/mobile/reply/ReactionMate.vue";
import BookIcon from "@/components/icon/BookIcon.vue";
import BaseImg from "./common/BaseImg.vue";
import escapeHtml from "@/modules/escape-html";
import { useReply } from "@/stores/reply";
import { GalGame } from "@/types/galGameEditor";
import { MateType } from "@/enums/mateEnums";

// Props & Emit
interface ThreadItemProps {
  moment?: Moment;
  uuid?: string; // moment.uuid
  mateId: string; // Mate UUID
  avatarUrl?: string;
  title: string;
  type?: MateType;
  time?: string;
  content?: string;
  mentionMatezz?: Pick<Mate, "uuid" | "name">[];
  mediaList?: Media[];
  showComment?: boolean;
  chatMessageList?: Pick<ChatMessage, "id" | "text" | "mate" | "dtCreate">[];
  enableReactionBtn?: boolean;
  replyTo?: Reply;
  fromMate?: Mate;
  enableTextCollapsed?: boolean;
  enableAudioPlay?: boolean;
  mateToneId?: number;
  visibility?: Visibility;
  showAvatar?: boolean; // 是否显示头像
  matchedText?: string; // 需要高亮的文本
  reply?: Reply;
  galGame?: GalGame;
  isSubReply?: boolean;
  entityType?: string;
  user?: Pick<User, "subscriptionType">;
  replyLength?: number;
}

const props: ThreadItemProps = withDefaults(defineProps<ThreadItemProps>(), {
  title: "",
  showComment: true,
  enableReactionBtn: true,
  enableTextCollapsed: true,
  enableAudioPlay: false,
  visibility: 1,
  showAvatar: true,
  isSubReply: false,
  replyLength: 0,
});
const emit = defineEmits<{
  (e: "threadClick", uuid: string): void;
  (e: "commentClick"): void;
  (e: "reactionClick"): void;
}>();
// const mateStore = useMate();
const replyStore = useReply();
// const toneId = computed(() => {
//   // 自己的角色更新Tone后，使用新的ToneId
//   const mateInMem = mateStore.mateMap.get(props.mateId);
//   if (mateInMem && mateInMem.mateToneId) {
//     return mateInMem.mateToneId;
//   }
//   return props.mateToneId ?? 0;
// });
const messageList = computed(() => {
  if (!props.chatMessageList || props.chatMessageList.length === 0) {
    return [];
  }
  return props.chatMessageList.map((item) => {
    return {
      id: item.id,
      currentUser: item.mate.uuid === props.mateId,
      avatarUrl: item.mate.mediaUrl,
      msg: item.text,
      name: item.mate.name,
    };
  });
});
const router = useRouter();
const route = useRoute();
const escapedContent = computed(() => {
  if (!props.content) {
    return "";
  }
  return escapeHtml(props.content);
});
const displayContent = computed(() => {
  if (!props.content) {
    return "";
  }
  // 1. 先按照换行符分割，保留7行
  const splitByLine = escapedContent.value.split("\n").slice(0, 7);
  // 2. 再按照字符数分割，保留前100个字符
  return splitByLine.join("\n").slice(0, 128);
});

const needCollapsed =
  props.enableTextCollapsed! &&
  !!props.content &&
  props.content!.length !== displayContent.value.length;

const textCollapsed = ref<boolean>(needCollapsed);

const contentHtml = computed(() => {
  if (!props.content) {
    return "";
  }

  const content = escapedContent.value.replace(/\n/g, "<br/>");
  if (props.matchedText) {
    const reg = new RegExp(props.matchedText, "g");
    return content.replace(reg, (matched) => {
      return `<span class="bg-yellow-500 text-neutral">${matched}</span>`;
    });
  }
  return content;
});

const isSingleLongImgMedia = computed(() => {
  return (
    props.mediaList &&
    props.mediaList.length === 1 &&
    (props.mediaList[0].type === 1 || isImage(props.mediaList[0].url)) &&
    (props.mediaList[0].height ?? 0) > (props.mediaList[0].width ?? 0)
  );
});

// Event Handler
const chatShareClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({
    name: "shareChatPreview",
    params: {
      uuid: props.uuid,
    },
  });
};
const onImageClick = (e: UIEvent, mediaList: Media[], idx: number) => {
  e.stopPropagation();
  showImagePreview({
    images: mediaList.map((item) => useRawImageUrl(item.url)),
    closeable: true,
    startPosition: idx,
  });
};
const onReactionClick = (e: UIEvent) => {
  e.stopPropagation();
  emit("reactionClick");
};

const routeToMateHome = (e: UIEvent, mateUuid: string) => {
  e.stopPropagation();
  router.push({
    name: "mateHome",
    params: { uuid: mateUuid },
    query: {
      isTreehole: 1,
    },
  });
};
const onStoryClick = (e: UIEvent) => {
  e.stopPropagation();
  router.push({
    name: "readStory",
    params: { uuid: props.moment!.story.uuid },
  });
};

const toMateDetail = (uuid: string) => {
  router.push({ name: "mateHome", params: { uuid: uuid } });
};
onActivated(() => {
  textCollapsed.value = needCollapsed;
});

const ownerTypeMap = {
  "App\\Models\\GalGame": {
    icon: "iconify mingcute--game-1-line",
    name: "game",
  },
  "App\\Models\\StoryBook": {
    icon: "iconify mingcute--book-2-line",
    name: "story",
  },
  "App\\Models\\Story": {
    icon: "iconify mingcute--book-2-line",
    name: "story",
  },
  "App\\Models\\Moment": {
    icon: "iconify mingcute--pencil-3-line",
    name: "moment",
  },
};

const replyOwner = computed(() => {
  const { isSubReply, reply, entityType } = props;
  const { id, rootId } = reply || {};
  if (!entityType || isSubReply || !id) return undefined;
  let owner = replyStore.replyOwnerMap[rootId || id];
  if (route.params.uuid === owner?.uuid) return undefined;
  return owner;
});

const clickOwnerItem = (type: string, uuid: string) => {
  if (type === "App\\Models\\GalGame")
    router.push({ name: "galGameDetail", params: { uuid } });
  else if (type === "App\\Models\\StoryBook")
    router.push({ name: "storyBookHome", params: { uuid } });
  else if (type === "App\\Models\\Story")
    router.push({ name: "storyHome", params: { uuid } });
  else if (type === "App\\Models\\Moment")
    router.push({ name: "threadDetail", params: { uuid } });
};

onMounted(() => {
  if (route.hash) {
    const replyId = route.hash.replace("#", "");
    if (replyId === props.reply?.id?.toString()) {
      // 高亮并闪烁动画
      const element = document.getElementById(`reply-${props.reply?.id}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        setTimeout(() => {
          element.classList.add("animate-pulse", "bg-primary/20", "rounded-lg");
          setTimeout(() => {
            element.classList.remove(
              "animate-pulse",
              "bg-primary/20",
              "rounded-lg"
            );
          }, 1200);
        }, 300);
      }
    }
  }
});
</script>
<template>
  <div class="flex flex-col w-full py-2 border-b">
    <div class="flex gap-2">
      <!-- Avatar -->
      <div v-if="showAvatar" class="flex flex-col items-center">
        <user-avatar
          :key="mateId"
          :avatar-url="avatarUrl"
          :mate="reply?.mate || moment?.mate"
          class="h-10 shrink-0"
          @click="(e) => routeToMateHome(e, mateId)"
        />
      </div>
      <!--  标题 & 类型 & 时间  -->
      <div class="flex-grow flex flex-col overflow-hidden">
        <div
          class="flex flex-col flex-grow gap-1"
          @click="emit('threadClick', uuid!)"
        >
          <div class="flex-grow">
            <div class="flex justify-between gap-1">
              <div class="flex flex-col">
                <div class="flex flex-col">
                  <div
                    v-if="title"
                    class="text-base-content font-bold"
                    @click="(e) => routeToMateHome(e, mateId)"
                  >
                    {{ title }}
                  </div>
                </div>
                <div v-if="replyTo" class="text-xs">
                  {{ $t("Reply.reply") }}
                  <span
                    class="text-base-content/50"
                    @click="(e) => routeToMateHome(e, replyTo!.mate.uuid)"
                  >
                    @{{ replyTo.mate.name }}
                  </span>
                </div>
              </div>
              <div class="gap-2 flex text-xs text-base-content/50">
                <div v-if="visibility != 1" class="badge badge-neutral">
                  {{ useVisibilityText(visibility) }}
                </div>
                <div v-if="time">{{ timeAgo(time) }}</div>
              </div>
            </div>
            <!-- 文本内容 -->
            <div
              v-if="content && !matchedText"
              class="text-base-content whitespace-pre-line"
            >
              <html-ellipsis
                v-if="props.enableTextCollapsed"
                :collapse-text="$t('Buttons.collapse')"
                :content="content"
                :expand-text="$t('Buttons.viewAll')"
                :rows="5"
              />
              <div
                v-else
                class="whitespace-pre-line break-words prose prose-sm same-size"
                v-html="content"
              />
              <div
                v-if="mentionMatezz && mentionMatezz.length > 0"
                class="mt-2 flex gap-2 flex-wrap"
              >
                <Tag
                  v-for="mentionMate in mentionMatezz"
                  :key="mentionMate.uuid"
                  class="badge badge-neutral"
                  @click.stop="toMateDetail(mentionMate.uuid)"
                >
                  @{{ mentionMate.name }}
                </Tag>
              </div>
            </div>
            <div
              v-else-if="content && matchedText"
              class="text-base-content whitespace-pre-line"
              v-html="contentHtml"
            ></div>
          </div>
          <!--  图片内容 -->
          <div
            v-if="mediaList && mediaList.length > 0"
            :class="{
              'grid-cols-[repeat(auto-fit,minmax(90px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(150px,1fr))]':
                !isSingleLongImgMedia,
              'grid-cols-2': isSingleLongImgMedia,
            }"
            class="grid gap-1"
          >
            <div
              v-for="(media, idx) in mediaList"
              :key="idx"
              class="rounded overflow-hidden max-h-[400px] sm:max-h-[600px] lg:max-h-[700px] relative"
              @click.stop="(e) => onImageClick(e, mediaList!, idx)"
            >
              <div
                v-if="
                  media.width &&
                  media.height &&
                  media.height / media.width > 1.5
                "
                class="absolute left-1 top-1 z-10 bg-black/50 backdrop-blur p-1 rounded text-white text-xs"
              >
                {{ $t("Message.longImage") }}
              </div>
              <BaseImg
                v-if="media.type === 1 || isImage(media.url)"
                :media="media"
                :width="
                  mediaList.length >= 3
                    ? 300
                    : mediaList.length === 2
                    ? 400
                    : undefined
                "
                class="w-full h-full rounded overflow-hidden"
                fit="cover"
              />
              <video
                v-else
                :poster="media.videoCoverUrl ?? ''"
                :src="media.url"
                class="max-w-full max-h-52"
                controls
                crossorigin="anonymous"
              />
            </div>
          </div>

          <div
            v-if="replyOwner"
            @click.stop="clickOwnerItem(replyOwner.entityType, replyOwner.uuid)"
          >
            <div class="badge text-xs gap-1 cursor-pointer flex">
              <i :class="[ownerTypeMap[replyOwner.entityType].icon]" />
              <span class="flex-1 line-clamp-1 max-w-[100px]">
                {{
                  $t(`Community.${ownerTypeMap[replyOwner.entityType].name}`)
                }}
                :
                {{ replyOwner.name || replyOwner.title || replyOwner.text }}
              </span>
            </div>
          </div>
          <div v-if="moment?.story" class="flex flex-col gap-2">
            <story-list-item
              :story="moment.story"
              class="border px-2 rounded"
              @click.stop="onStoryClick"
            />
            <div v-if="moment.story?.storyBook" class="flex gap-1 items-center">
              <div class="text-xs text-base-content/50">
                {{ $t("story.collectedOn") }}
              </div>
              <div
                class="badge badge-neutral text-xs gap-1 cursor-pointer"
                @click.stop="
                  $event.stopPropagation();
                  router.push({
                    name: 'storyBookHome',
                    params: {
                      uuid: moment.story?.storyBook?.uuid,
                    },
                  });
                "
              >
                <book-icon class="icon-xs" />
                <span>{{ moment.story?.storyBook?.title }}</span>
              </div>
            </div>
          </div>

          <div v-if="moment?.galGame" class="relative overflow-hidden mb-2.5">
            <BaseImg
              :media="moment.galGame.coverMedia"
              class="rounded-t-lg"
              ratio="16:9"
            />

            <div
              class="text-white bg-primary h-11 flex px-4 items-center justify-between rounded-b-lg"
            >
              <h1
                class="text-base font-bold text-ellipsis overflow-hidden whitespace-nowrap"
              >
                {{ moment.galGame.name }}
              </h1>
              <button
                class="btn btn-xs px-4"
                @click.stop="
                  router.push({
                    name: 'galGameDetail',
                    params: { uuid: moment.galGame.uuid },
                  })
                "
              >
                {{ $t("Buttons.gamePlay") }}
              </button>
            </div>
            <VanTextEllipsis
              :content="moment.galGame.description"
              class="p-1 text-my-gray"
              rows="3"
            />
          </div>
          <div
            v-if="messageList && messageList.length > 0"
            class="flex-col max-h-64 overflow-hidden border p-2 rounded relative thread-chat-share-item bg-base-200"
            @click.stop="chatShareClick"
          >
            <chat-message-item
              v-for="item in messageList.slice(0, 4)"
              :key="item.id"
              :avatar-url="item.avatarUrl"
              :msg="item.msg"
              :name="item.name"
              :position="item.currentUser ? 'end' : 'start'"
              class="flex-grow"
            />
          </div>
          <div
            v-if="fromMate"
            class="flex items-center text-xs text-base-content/50 gap-1"
          >
            <span>{{ $t("Moment.collaborativeCreation") }}</span>
            <span
              class="badge badge-primary badge-sm"
              @click="(e) => routeToMateHome(e, fromMate!.uuid)"
            >
              @{{ fromMate.name }}
            </span>
          </div>
          <!-- 交互 -->
          <div class="flex flex-col gap-2">
            <reaction-mate :reply="reply" />
            <div class="flex w-full justify-between items-end">
              <div class="flex gap-2 flex-wrap">
                <button
                  v-if="
                    showComment &&
                    !(reply?.mateId === moment?.mate.id) &&
                    replyLength < 5
                  "
                  class="btn btn-xs font-normal rounded"
                  @click.stop="emit('commentClick')"
                >
                  <i class="iconify mingcute--comment-line" />
                  <span v-if="reply && reply.replyCount > 0">
                    ({{ reply?.replyCount }})
                  </span>
                </button>
                <reaction-counter :reply="reply" />
              </div>
              <div class="flex gap-1 items-center">
                <div
                  v-if="enableReactionBtn"
                  class="btn btn-ghost btn-xs btn-square rounded"
                  @click.stop="onReactionClick"
                >
                  <i class="iconify mingcute--more-1-line" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
